/**
 * Collection filters: AJAX apply.
 * - Desktop left sidebar: no Apply/Clear footer; apply on each checkbox change (AJAX).
 * - Drawer (mobile / filter-bar-only): Apply + Clear All in footer; apply only on Apply click.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';

const SHOW_MORE_CHECKBOX_CLASS = 'main-collection__show-more-checkbox';
const OVERFLOW_TEMPLATE_SELECTOR = 'template[data-filter-overflow]';
/** Align with main-collection sidebar: sticky left column is min-width 1024px; below that uses the mobile drawer. */
const DESKTOP_BREAKPOINT = 1024;
/**
 * Desktop sidebar applies on every checkbox change, and each apply re-renders the whole
 * main-collection section (toolbar + grid + all facets) over the network. 150ms was short
 * enough that ticking two boxes in a row fired two full renders and the first one's work
 * was thrown away, so multi-select felt roughly twice as slow as it needed to.
 */
const APPLY_DEBOUNCE_MS = 350;

/** Scroll back to a linked product card after returning to the collection. */
function scrollToHashCard() {
  if (!window.location.hash) return;
  const card = document.getElementById(window.location.hash.slice(1));
  card?.scrollIntoView({ behavior: 'instant' });
}

/** Preserve the current products-per-page selection in facet submissions. */
function ensureLimitInput(form) {
  const limit = new URLSearchParams(window.location.search).get('limit');
  if (!limit || form.querySelector('input[name="limit"]')) return;

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'limit';
  input.value = limit;
  form.appendChild(input);
}

/** Count facet values still parked in an un-inlined overflow <template>. */
function pendingOverflowCount(scope) {
  let total = 0;
  scope?.querySelectorAll?.(OVERFLOW_TEMPLATE_SELECTOR).forEach((tpl) => {
    total += Number(tpl.getAttribute('data-filter-overflow-count')) || tpl.content.children.length;
  });
  return total;
}

/**
 * Inline the facet values withheld from the initial DOM.
 * filters.liquid parks every unselected value past the first 5 in a <template> so a wide
 * sidebar does not ship hundreds of display:none elements on first paint. Call this before
 * anything that needs to see or match the full value list.
 * @returns {boolean} true when a template was inlined
 */
function hydrateFilterOverflow(scope) {
  const templates = scope?.querySelectorAll?.(OVERFLOW_TEMPLATE_SELECTOR);
  if (!templates?.length) return false;
  templates.forEach((tpl) => {
    const parent = tpl.parentNode;
    if (!parent) return;
    parent.insertBefore(tpl.content.cloneNode(true), tpl);
    tpl.remove();
  });
  return true;
}

function updateShowMoreForSearch(group) {
  if (!group) return;
  const searchInput = group.querySelector('.main-collection__filter-search');
  const showMoreWrap = group.querySelector('.main-collection__filter-list-footer-left');
  const showMoreCheckbox = group.querySelector(`.${SHOW_MORE_CHECKBOX_CLASS}`);
  const showMoreText = group.querySelector('.main-collection__show-more-text--more');
  if (!showMoreWrap || !showMoreCheckbox || !showMoreText) return;

  const hasSearch = Boolean(searchInput?.value.trim());
  // Parked values count toward "Show N more" even before the template is inlined.
  const count =
    group.querySelectorAll('.main-collection__filter-option--hidden').length + pendingOverflowCount(group);
  showMoreWrap.style.display = hasSearch || count === 0 ? 'none' : '';
  showMoreText.textContent = count === 1 ? 'Show 1 more' : `Show ${count} more`;
}

function initializeFacetControls(form) {
  if (!form) return;
  // Re-run the facet count cache: filters.liquid ships it inline (this module is lazy-loaded)
  // and inline scripts do not re-execute after a section morph.
  /** @type {any} */ (window).__tjcFacetCounts?.sync();
  ensureLimitInput(form);
  syncCollectionFacetExpansion(form);
  form.querySelectorAll('.main-collection__filter-group').forEach((group) => {
    if (group.querySelector('.main-collection__filter-search')) updateShowMoreForSearch(group);
  });
}

/** True when filters are in the left sidebar (desktop, not filter-bar-only). */
function isDesktopSidebar() {
  const main = document.querySelector('.main-collection');
  return (
    window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches &&
    main &&
    !main.classList.contains('main-collection--filter-bar-only')
  );
}

/**
 * Desktop sidebar: set expanded/collapsed from data attributes.
 * When data-desktop-expansion-custom is true, opens the first data-desktop-expanded-count groups (theme: 2 when merchants enable "all closed" desktop mode).
 * Otherwise, if mobile drawer defaults are collapsed, expands every group on desktop (legacy).
 * Runs on load and after section morph (FilterUpdate) because inline scripts in the section do not re-execute.
 * @param {HTMLFormElement | null} form
 */
function syncCollectionFacetExpansion(form) {
  if (!form) return;
  const mobileDefault = form.getAttribute('data-mobile-filters-default') || 'closed';
  const desktopExpansionCustom = (form.getAttribute('data-desktop-expansion-custom') || '').trim() === 'true';
  const rawCount = form.getAttribute('data-desktop-expanded-count');
  const parsed =
    rawCount != null && String(rawCount).trim() !== '' ? parseInt(String(rawCount).trim(), 10) : NaN;
  const desktopExpandedCount = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  const desktopSidebar = isDesktopSidebar();

  /**
   * @param {Element} group
   * @param {boolean} expand
   */
  function setFilterGroupExpanded(group, expand) {
    const content = group.querySelector('.main-collection__filter-group-content');
    const header = group.querySelector('.main-collection__filter-group-header');
    const arrow = group.querySelector('.main-collection__filter-arrow');
    group.classList.remove('main-collection__filter-group--user-collapsed');
    if (expand) {
      group.classList.add('main-collection__filter-group--expanded');
      if (header) header.setAttribute('aria-expanded', 'true');
      if (content) content.style.removeProperty('max-height');
      if (arrow) arrow.style.transform = 'rotate(180deg)';
    } else {
      group.classList.remove('main-collection__filter-group--expanded');
      if (header) header.setAttribute('aria-expanded', 'false');
      if (content) content.style.maxHeight = '0';
      if (arrow) arrow.style.transform = 'rotate(0deg)';
    }
  }

  if (desktopSidebar && desktopExpansionCustom) {
    const groups = form.querySelectorAll('.main-collection__filter-group');
    const n = desktopExpandedCount;
    groups.forEach((group, index) => {
      setFilterGroupExpanded(group, index < n);
    });
  } else if (mobileDefault === 'closed' && desktopSidebar) {
    form.querySelectorAll('.main-collection__filter-group').forEach((group) => {
      setFilterGroupExpanded(group, true);
    });
  }
}

/**
 * Build filter URL from form state (checkboxes, price range, sort_by, limit).
 * @param {HTMLFormElement} form
 * @param {URL} currentUrl
 * @returns {string} Full URL string
 */
function buildFilterUrl(form, currentUrl) {
  const searchParams = new URLSearchParams();

  const limitParam = currentUrl.searchParams.get('limit');
  if (limitParam) searchParams.append('limit', limitParam);

  const viewParam = currentUrl.searchParams.get('view');
  if (viewParam) searchParams.append('view', viewParam);

  const sortByParam = currentUrl.searchParams.get('sort_by');
  if (sortByParam) searchParams.append('sort_by', sortByParam);

  const checkboxes = form.querySelectorAll('input[type="checkbox"]:checked');
  checkboxes.forEach((checkbox) => {
    if (checkbox.classList.contains(SHOW_MORE_CHECKBOX_CLASS)) return;
    if (checkbox.name && checkbox.value) searchParams.append(checkbox.name, checkbox.value);
  });

  const priceRangeContainers = form.querySelectorAll('.main-collection__price-range');
  priceRangeContainers.forEach((container) => {
    const minInput = container.querySelector('.main-collection__price-range-input--min');
    const maxInput = container.querySelector('.main-collection__price-range-input--max');
    if (!minInput || !maxInput) return;
    const rangeMin = parseFloat(minInput.getAttribute('min')) || 0;
    const rangeMax = parseFloat(maxInput.getAttribute('max')) || 0;
    const minValue = minInput.value !== '' ? parseFloat(minInput.value) : rangeMin;
    const maxValue = maxInput.value !== '' ? parseFloat(maxInput.value) : rangeMax;
    if ((minValue !== rangeMin || maxValue !== rangeMax) && minInput.name && maxInput.name) {
      if (minValue !== rangeMin) searchParams.append(minInput.name, minInput.value);
      if (maxValue !== rangeMax) searchParams.append(maxInput.name, maxInput.value);
    }
  });

  const query = searchParams.toString();
  return `${currentUrl.pathname}${query ? `?${query}` : ''}`;
}

/**
 * Close the filter drawer if open.
 */
function closeDrawer() {
  const sidebar = document.querySelector('.main-collection__sidebar');
  const overlay = document.querySelector('[data-collection-filter-overlay]');
  if (sidebar) sidebar.classList.remove('main-collection__sidebar--open');
  if (overlay) overlay.classList.remove('backdrop-scrim--visible');
  document.body.style.overflow = '';
}

/**
 * Toggle mobile filter drawer open/closed.
 */
function toggleFilterDrawer() {
  const sidebar = document.querySelector('.main-collection__sidebar');
  const overlay = document.querySelector('[data-collection-filter-overlay]');
  if (!sidebar) return;
  sidebar.classList.toggle('main-collection__sidebar--open');
  if (overlay) overlay.classList.toggle('backdrop-scrim--visible');
  document.body.style.overflow = sidebar.classList.contains('main-collection__sidebar--open') ? 'hidden' : '';
}

const RATING_OVERLAY_CONTENT_SELECTOR =
  '.main-collection .product-card[data-rating-overlay-on-image="true"] .product-card__content';

/**
 * Move review stars onto the gallery image (bottom-left).
 * assets/collection-plp-deferred-tiles.js settleRatingOverlay (theme setting perf_plp_tile_card_settled)
 * makes the same move on cards loaded after the page; change both together.
 */
function applyRatingOverlayOnImage(contents = document.querySelectorAll(RATING_OVERLAY_CONTENT_SELECTOR)) {
  contents.forEach((content) => {
    const reviewBlock = content.querySelector('.review-block');
    const gallery = content.querySelector('.card-gallery');
    if (!reviewBlock || !gallery || reviewBlock.parentElement === gallery) return;
    reviewBlock.classList.add('card-gallery__rating-overlay');
    gallery.appendChild(reviewBlock);
  });
}

function initRatingOverlay() {
  const pendingContents = new Set();
  let ratingOverlayFrame = null;
  let productsContainer = null;
  const ratingOverlayObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      const content = target?.closest(RATING_OVERLAY_CONTENT_SELECTOR);
      if (content) pendingContents.add(content);
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        const addedContent = node.closest(RATING_OVERLAY_CONTENT_SELECTOR);
        if (addedContent) pendingContents.add(addedContent);
        else node.querySelectorAll(RATING_OVERLAY_CONTENT_SELECTOR).forEach((item) => pendingContents.add(item));
      });
    });
    if (!pendingContents.size || ratingOverlayFrame !== null) return;
    // Repeated app mutations on one card need only one affected-card check per frame.
    ratingOverlayFrame = requestAnimationFrame(() => {
      ratingOverlayFrame = null;
      const contents = [...pendingContents].filter((content) =>
        content.isConnected && productsContainer?.contains(content) && content.matches(RATING_OVERLAY_CONTENT_SELECTOR)
      );
      pendingContents.clear();
      applyRatingOverlayOnImage(contents);
    });
  });
  const reconnectRatingOverlay = () => {
    ratingOverlayObserver.disconnect();
    if (ratingOverlayFrame !== null) cancelAnimationFrame(ratingOverlayFrame);
    ratingOverlayFrame = null;
    pendingContents.clear();
    applyRatingOverlayOnImage();
    productsContainer = document.querySelector('.main-collection__products');
    if (productsContainer) {
      ratingOverlayObserver.observe(productsContainer, { childList: true, subtree: true });
    }
  };
  reconnectRatingOverlay();
  document.addEventListener('shopify:section:load', reconnectRatingOverlay);
  document.addEventListener('shopify:section:unload', reconnectRatingOverlay);
  document.addEventListener('shopify:block:select', reconnectRatingOverlay);
  return reconnectRatingOverlay;
}

/**
 * True if the form has any filter selection (checked filter checkbox or non-default price range).
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function hasFilterSelections(form) {
  const checkboxes = form.querySelectorAll(
    `input[type="checkbox"]:checked:not(.${SHOW_MORE_CHECKBOX_CLASS})`
  );
  if (checkboxes.length > 0) return true;

  const priceRangeContainers = form.querySelectorAll('.main-collection__price-range');
  for (const container of priceRangeContainers) {
    const minInput = container.querySelector('.main-collection__price-range-input--min');
    const maxInput = container.querySelector('.main-collection__price-range-input--max');
    if (!minInput || !maxInput) continue;
    const rangeMin = parseFloat(minInput.getAttribute('min')) || 0;
    const rangeMax = parseFloat(maxInput.getAttribute('max')) || 0;
    const minValue = minInput.value !== '' ? parseFloat(minInput.value) : rangeMin;
    const maxValue = maxInput.value !== '' ? parseFloat(maxInput.value) : rangeMax;
    if (minValue !== rangeMin || maxValue !== rangeMax) return true;
  }
  return false;
}

/**
 * Toggle footer between "Close" only and "Clear Filter" + "Apply Filter" based on form state.
 * @param {HTMLFormElement} form
 */
function updateFooterState(form) {
  const footer = document.querySelector('.main-collection__sidebar-footer');
  if (!footer) return;
  const closeWrap = footer.querySelector('.main-collection__sidebar-footer-close-wrap');
  const actions = footer.querySelector('.main-collection__sidebar-footer-actions');
  if (!closeWrap || !actions) return;

  const hasSelections = hasFilterSelections(form);
  footer.dataset.footerState = hasSelections ? 'has-selections' : '';
  closeWrap.hidden = hasSelections;
  actions.hidden = !hasSelections;
}

/**
 * Apply filters via AJAX: fetch section HTML, morph, update history, close drawer, dispatch FilterUpdate.
 * @param {string} sectionId - Section instance id (e.g. template--123__main-collection)
 * @param {string} urlString - Full URL (pathname + search) to apply
 */
async function applyFilters(sectionId, urlString) {
  const url = new URL(urlString, window.location.origin);
  const main = document.querySelector('.main-collection');
  // Instant feedback: the section render is a network round trip, so without this a click
  // looks like it did nothing until the new grid lands.
  main?.setAttribute('data-filters-busy', 'true');
  try {
    await sectionRenderer.renderSection(sectionId, { cache: false, url });
  } finally {
    document.querySelector('.main-collection')?.removeAttribute('data-filters-busy');
  }
  history.pushState({}, '', urlString);
  closeDrawer();
  document.dispatchEvent(new FilterUpdateEvent(url.searchParams));
}

let activeFilterPill = null;

function closeFilterPill(restoreFocus = false) {
  if (!activeFilterPill) return;
  const { button, panel, group, placeholder } = activeFilterPill;
  activeFilterPill = null;
  placeholder.replaceWith(group);
  panel.hidden = true;
  button.setAttribute('aria-expanded', 'false');
  if (restoreFocus && button.isConnected) button.focus();
}

function syncPillPriceRange(group, input) {
  const min = group.querySelector('.main-collection__price-range-input--min');
  const max = group.querySelector('.main-collection__price-range-input--max');
  if (!min || !max) return;
  const minSlider = group.querySelector('.main-collection__price-range-slider--min');
  const maxSlider = group.querySelector('.main-collection__price-range-slider--max');
  if (input === minSlider) min.value = input.value;
  if (input === maxSlider) max.value = input.value;
  let low = min.value === '' ? Number(min.min) : Number(min.value);
  let high = max.value === '' ? Number(max.max) : Number(max.value);
  if (low > high) {
    if (input === min || input === minSlider) min.value = String(low = high);
    else max.value = String(high = low);
  }
  if (minSlider) minSlider.value = String(low);
  if (maxSlider) maxSlider.value = String(high);
  const progress = group.querySelector('.main-collection__price-range-slider-progress');
  const span = Number(max.max) - Number(min.min);
  if (progress && span > 0) {
    progress.style.left = `${(low - Number(min.min)) / span * 100}%`;
    progress.style.right = `${(Number(max.max) - high) / span * 100}%`;
  }
  const values = group.querySelector('.main-collection__price-range-values');
  const currency = group.querySelector('.main-collection__price-range-currency')?.textContent || '';
  if (values) values.textContent = `${currency}${low} - ${currency}${high}`;
}

/** Reuse the actual sidebar groups, including locked-facet rules and current selections. */
function initFilterPills() {
  closeFilterPill();
  const host = document.querySelector('[data-filter-pills]');
  const form = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
  if (!host || !form) return;
  host.replaceChildren();
  const maxPills = Number(host.dataset.maxPills) || Infinity;
  let pillCount = 0;
  form.querySelectorAll(':scope > .main-collection__filter-group').forEach((group, index) => {
    if (pillCount >= maxPills) return;
    const name = group.querySelector('.main-collection__filter-group-name')?.textContent.trim();
    if (!name) return;
    pillCount += 1;
    const count = group.querySelector('.main-collection__filter-group-count')?.textContent.trim();
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'main-collection__filter-pill';
    button.textContent = `${name}${count ? ` ${count}` : ''}`;
    button.id = `${form.id}-pill-${index}`;
    button.setAttribute('aria-expanded', 'false');
    const panel = document.createElement('div');
    panel.className = 'main-collection__pill-panel';
    panel.hidden = true;
    panel.id = `${button.id}-panel`;
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-labelledby', button.id);
    button.setAttribute('aria-controls', panel.id);
    host.append(button, panel);

    const apply = () => {
      const invalid = panel.querySelector('input:invalid');
      if (invalid) { invalid.reportValidity(); return; }
      // Put every input back in the one source form before serializing its filters.
      closeFilterPill();
      form.requestSubmit();
    };
    button.addEventListener('click', () => {
      const wasOpen = activeFilterPill?.button === button;
      closeFilterPill();
      if (wasOpen) return;
      hydrateFilterOverflow(group);
      const placeholder = document.createComment('Filter group position');
      group.replaceWith(placeholder);
      panel.appendChild(group);
      panel.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      activeFilterPill = { button, panel, group, placeholder };
      const rect = button.getBoundingClientRect();
      panel.style.left = `${Math.max(12, Math.min(rect.left, window.innerWidth - panel.offsetWidth - 12))}px`;
      panel.style.top = `${rect.bottom + 8}px`;
      panel.style.setProperty('--pill-panel-height', `${Math.max(100, window.innerHeight - rect.bottom - 24)}px`);
      panel.querySelector('input:not([disabled]):not([type="hidden"])')?.focus({ preventScroll: true });
    });
    panel.addEventListener('change', (event) => {
      const input = event.target;
      if (input.classList.contains(SHOW_MORE_CHECKBOX_CLASS)) {
        hydrateFilterOverflow(group);
        updateShowMoreForSearch(group);
        return;
      }
      if (input.matches('input[type="checkbox"], .main-collection__price-range input')) {
        event.stopPropagation();
        if (input.closest('.main-collection__price-range')) syncPillPriceRange(group, input);
        apply();
      }
    });
    panel.addEventListener('input', (event) => {
      if (event.target.closest('.main-collection__price-range')) syncPillPriceRange(group, event.target);
    });
    panel.addEventListener('click', (event) => {
      if (!event.target.closest('.main-collection__filter-reset')) return;
      event.preventDefault();
      event.stopPropagation();
      group.querySelectorAll('input[type="checkbox"]').forEach((input) => { input.checked = false; });
      apply();
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('input:not([type="checkbox"])')) {
        event.preventDefault();
        apply();
      }
    });
  });
}

function init() {
  initFilterPills();
  document.addEventListener('shopify:section:load', initFilterPills);
  document.addEventListener('click', (event) => {
    if (activeFilterPill && !activeFilterPill.panel.contains(event.target) && !activeFilterPill.button.contains(event.target)) {
      closeFilterPill();
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeFilterPill) {
      event.preventDefault();
      closeFilterPill(true);
    }
  });
  document.addEventListener('focusin', (event) => {
    if (activeFilterPill && !activeFilterPill.panel.contains(event.target) && event.target !== activeFilterPill.button) closeFilterPill();
  });
  window.addEventListener('resize', () => closeFilterPill());
  window.addEventListener('scroll', (event) => {
    if (activeFilterPill && !activeFilterPill.panel.contains(event.target)) closeFilterPill();
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-collection-filter-overlay], .main-collection__sidebar-close')) closeDrawer();
  });
  const reconnectRatingOverlay = initRatingOverlay();
  scrollToHashCard();

  document.addEventListener('click', (e) => {
    const header = e.target?.closest?.('.main-collection__filter-group-header');
    if (!header) return;
    const form = header.closest('form[id^="FacetFiltersForm-"]');
    const group = header.closest('.main-collection__filter-group');
    if (!form || !group) return;

    const desktopSidebar = isDesktopSidebar();
    const useUserCollapsedDesktop =
      form.classList.contains('main-collection__filters-form--collapse-on-mobile') &&
      desktopSidebar &&
      !form.classList.contains('main-collection__filters-form--desktop-expansion-custom');
    if (desktopSidebar) {
      group.classList.toggle(
        useUserCollapsedDesktop
          ? 'main-collection__filter-group--user-collapsed'
          : 'main-collection__filter-group--expanded'
      );
    } else {
      group.classList.toggle('main-collection__filter-group--mobile-expanded');
    }

    const expanded = desktopSidebar
      ? useUserCollapsedDesktop
        ? !group.classList.contains('main-collection__filter-group--user-collapsed')
        : group.classList.contains('main-collection__filter-group--expanded')
      : group.classList.contains('main-collection__filter-group--mobile-expanded');
    const content = group.querySelector('.main-collection__filter-group-content');
    const arrow = header.querySelector('.main-collection__filter-arrow');
    if (expanded) content?.style.removeProperty('max-height');
    else if (content) content.style.maxHeight = '0';
    header.setAttribute('aria-expanded', String(expanded));
    if (arrow) arrow.style.transform = expanded ? 'rotate(180deg)' : 'rotate(0deg)';
  });

  // Use delegation so handlers work after section morph (AJAX apply)
  document.addEventListener('submit', (e) => {
    const form = e.target?.closest?.('.main-collection form[id^="FacetFiltersForm-"]');
    if (!form) return;
    e.preventDefault();
    const sectionId = form.id.replace('FacetFiltersForm-', '');
    const currentUrl = new URL(window.location.href);
    const urlString = buildFilterUrl(form, currentUrl);
    applyFilters(sectionId, urlString);
  });

  document.addEventListener('click', (e) => {
    if (e.target?.closest?.('.main-collection__sidebar-footer-close')) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    const clearBtn = e.target?.closest?.('.main-collection__sidebar-footer-clear');
    if (clearBtn) {
      e.preventDefault();
      const footer = document.querySelector('.main-collection__sidebar-footer');
      const form = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
      const collectionBaseUrl = footer?.dataset.collectionUrl || (form && new URL(form.action || window.location.href).pathname) || window.location.pathname;
      const sectionId = form?.id?.replace('FacetFiltersForm-', '');
      if (!sectionId) return;
      const urlString = collectionBaseUrl.startsWith('http') ? new URL(collectionBaseUrl).pathname : collectionBaseUrl;
      applyFilters(sectionId, urlString);
      return;
    }
    const resetBtn = e.target?.closest?.('.main-collection__filter-reset');
    if (resetBtn) {
      e.preventDefault();
      const filterListId = resetBtn.getAttribute('data-filter-list');
      const form = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
      if (!filterListId || !form) return;
      const list = form.querySelector(`.main-collection__filter-checkbox-list[data-filter-list="${filterListId}"]`);
      if (!list) return;
      list.querySelectorAll('.main-collection__filter-checkbox').forEach((cb) => {
        cb.checked = false;
      });
      const showMoreCheckbox = list.querySelector(`.${SHOW_MORE_CHECKBOX_CLASS}`);
      if (showMoreCheckbox) showMoreCheckbox.checked = false;
      updateFooterState(form);
      const sectionId = form.id.replace('FacetFiltersForm-', '');
      const currentUrl = new URL(window.location.href);
      const urlString = buildFilterUrl(form, currentUrl);
      applyFilters(sectionId, urlString);
    }
  });

  document.addEventListener('change', (e) => {
    const form = e.target?.closest?.('.main-collection form[id^="FacetFiltersForm-"]');
    if (!form) return;
    if (e.target?.classList?.contains(SHOW_MORE_CHECKBOX_CLASS)) {
      const group = e.target.closest('.main-collection__filter-group');
      hydrateFilterOverflow(group);
      const content = group?.querySelector('.main-collection__filter-group-content');
      if (content) content.style.removeProperty('max-height');
      updateShowMoreForSearch(group);
    }
    const isFilterInput =
      e.target?.matches?.(`input[type="checkbox"]:not(.${SHOW_MORE_CHECKBOX_CLASS})`) ||
      e.target?.matches?.('.main-collection__price-range-input--min, .main-collection__price-range-input--max');
    if (isFilterInput) {
      updateFooterState(form);
    }
    if (isDesktopSidebar() && isFilterInput) {
      if (window.__collectionFiltersApplyTimeout) clearTimeout(window.__collectionFiltersApplyTimeout);
      window.__collectionFiltersApplyTimeout = setTimeout(() => {
        window.__collectionFiltersApplyTimeout = null;
        const sectionId = form.id.replace('FacetFiltersForm-', '');
        const currentUrl = new URL(window.location.href);
        const urlString = buildFilterUrl(form, currentUrl);
        applyFilters(sectionId, urlString);
      }, APPLY_DEBOUNCE_MS);
    }
  });

  document.addEventListener('input', (e) => {
    if (e.target?.matches?.('.main-collection__filter-search')) {
      const input = e.target;
      const group = input.closest('.main-collection__filter-group');
      hydrateFilterOverflow(group);
      const term = input.value.trim().toLowerCase();
      let visibleCount = 0;
      group?.querySelectorAll('.main-collection__filter-option').forEach((option) => {
        const matches = !term || option.textContent.toLowerCase().includes(term);
        option.style.display = matches ? (term ? 'flex' : '') : 'none';
        if (matches) visibleCount++;
      });
      const noResults = group?.querySelector('.main-collection__filter-no-results');
      if (noResults) noResults.hidden = !term || visibleCount > 0;
      updateShowMoreForSearch(group);
      return;
    }
    if (!e.target?.matches?.('.main-collection__price-range-input--min, .main-collection__price-range-input--max')) return;
    const form = e.target?.closest?.('.main-collection form[id^="FacetFiltersForm-"]');
    if (form) updateFooterState(form);
  });

  document.addEventListener('focusin', (e) => {
    if (e.target?.matches?.('.main-collection__filter-search')) {
      const group = e.target.closest('.main-collection__filter-group');
      hydrateFilterOverflow(group);
      updateShowMoreForSearch(group);
    }
  });

  // Initial footer state (facet expansion: filters.liquid DOMContentLoaded + FilterUpdate — inline scripts do not run after morph)
  const form = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
  if (form) {
    initializeFacetControls(form);
    updateFooterState(form);
  }

  document.addEventListener(ThemeEvents.FilterUpdate, () => {
    requestAnimationFrame(() => {
      const f = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
      if (f) initializeFacetControls(f);
      initFilterPills();
      reconnectRatingOverlay();
    });
  });

  document.addEventListener('click', (e) => {
    if (!e.target?.closest?.('.main-collection__filter-button')) return;
    toggleFilterDrawer();
    setTimeout(() => {
      const f = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
      if (f) updateFooterState(f);
    }, 0);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
